import { combineReducers } from 'redux';
import {
  ADD_MESSAGE,
  UPDATE_MESSAGE,
  DELETE_MESSAGE,
  UPDATE_MESSAGES,
  CACHE_MESSAGE,
  POP_MESSAGE,
  CLEAR_MESSAGES,
  ADD_REACTION_TO_MESSAGE,
  REMOVE_REACTION_FROM_MESSAGE,
  UPDATE_MESSAGE_REACTIONS,
  REPLACE_MESSAGE,
} from './messages-actions';

const messagesById = (state = {}, action) => {
  switch (action.type) {
    case UPDATE_MESSAGE:
    case ADD_MESSAGE:
      return {
        ...state,
        [action.payload.message.id]: action.payload.message,
      };
    case UPDATE_MESSAGES:
      const updateMessagesState = { ...state };
      action.payload.messages.forEach((message) => {
        updateMessagesState[message.id] = message;
      });
      return updateMessagesState;
    case DELETE_MESSAGE:
      const removeMessagesState = { ...state };
      delete removeMessagesState[action.payload.id];
      return removeMessagesState;
    case ADD_REACTION_TO_MESSAGE:
    case REMOVE_REACTION_FROM_MESSAGE:
      const { messageId, reactionType, count, userId } = action.payload;
      const targetMessage = state[messageId];
      
      if (!targetMessage) {
        return state;
      }
      
      // Initialize reactions and userReactions if they don't exist
      const currentReactions = targetMessage.reactions || {};
      const currentUserReactions = targetMessage.userReactions || {};
      
      const updatedMessage = {
        ...targetMessage,
        reactions: {
          ...currentReactions,
          [reactionType]: count
        },
        userReactions: {
          ...currentUserReactions,
          // For ADD, set this user as having this reaction type
          // For REMOVE, clear this user's reaction if it was theirs
          [reactionType]: action.type === ADD_REACTION_TO_MESSAGE ? userId : 
                         (currentUserReactions[reactionType] === userId ? null : currentUserReactions[reactionType])
        }
      };
      
      // Clean up if count is 0
      if (count === 0) {
        delete updatedMessage.reactions[reactionType];
        delete updatedMessage.userReactions[reactionType];
      }
      
      return {
        ...state,
        [messageId]: updatedMessage
      };
    case UPDATE_MESSAGE_REACTIONS:
      const message = state[action.payload.messageId];
      if (!message) return state;
      
      return {
        ...state,
        [action.payload.messageId]: {
          ...message,
          reactions: action.payload.reactions,
          userReactions: action.payload.userReactions || message.userReactions || {}
        }
      };
    case REPLACE_MESSAGE:
      const { tempId, realMessage } = action.payload;
      
      // Get the temporary message
      const tempMessage = state[tempId];
      if (!tempMessage) {
        console.warn(`Temp message ${tempId} not found for replacement`);
        // Still add the real message even if temp not found (edge case handling)
        return {
          ...state,
          [realMessage.id]: realMessage
        };
      }
      
      // Create new state without the temp message and with the real message
      const newState = { ...state };
      delete newState[tempId];  // Remove temp message
      newState[realMessage.id] = {
        ...realMessage,
        // Preserve any local state from temp message (like fade animations, positioning, and original properties)
        rect: tempMessage.rect,
        fontSize: tempMessage.fontSize,  // Preserve the calculated font size for classic view
        triggerFadeOut: tempMessage.triggerFadeOut,
        fadeOutDuration: tempMessage.fadeOutDuration,
        createdAt: tempMessage.createdAt  // Keep original timestamp to prevent jumping
      };
      
      return newState;
    case CLEAR_MESSAGES:
      return {};
    default:
      return state;
  }
};

const allMessages = (state = [], action) => {
  switch (action.type) {
    case ADD_MESSAGE:
      return state.concat(action.payload.message.id);
    case DELETE_MESSAGE:
      return state.filter((id) => id !== action.payload.id);
    case REPLACE_MESSAGE:
      // Replace the temp ID with the real ID in the array
      const { tempId, realMessage } = action.payload;
      return state.map(id => id === tempId ? realMessage.id : id);
    case CLEAR_MESSAGES:
      return [];
    default:
      return state;
  }
};

const CACHE_LENGTH = 5;

const cacheMessages = (state = [], action) => {
  switch (action.type) {
    case CACHE_MESSAGE:
      if (state.length >= CACHE_LENGTH) {
        return state.slice(1).concat(action.payload.message);
      }
      return state.concat(action.payload.message);
    case POP_MESSAGE:
      return state.slice(0, -1);
    case CLEAR_MESSAGES:
      return [];
    default:
      return state;
  }
};

export default combineReducers({
  byId: messagesById,
  allIds: allMessages,
  cached: cacheMessages,
});
